import { useState } from 'react';
import { apiFetch } from '../../utils/api';

const DIAL_CODES = [
  { code: '+263', label: '+263  Zimbabwe' },
  { code: '+27',  label: '+27   South Africa' },
  { code: '+260', label: '+260  Zambia' },
  { code: '+267', label: '+267  Botswana' },
  { code: '+254', label: '+254  Kenya' },
  { code: '+255', label: '+255  Tanzania' },
  { code: '+234', label: '+234  Nigeria' },
  { code: '+1',   label: '+1    USA / Canada' },
  { code: '+44',  label: '+44   United Kingdom' },
  { code: '+91',  label: '+91   India' },
  { code: '+61',  label: '+61   Australia' },
];

export default function SendInviteSection({ botId, userName }: { botId: string; userName: string }) {
  const [dialCode, setDialCode] = useState('+263');
  const [phone, setPhone]       = useState('');
  const [status, setStatus]     = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errMsg, setErrMsg]     = useState('');
  const [errAction, setErrAction] = useState<{ label: string; url: string } | null>(null);

  async function handleSend() {
    const cleaned = phone.replace(/\D/g, '');
    if (!cleaned) return;
    const fullNumber = dialCode.replace('+', '') + cleaned;
    setStatus('sending');
    setErrMsg('');
    setErrAction(null);
    try {
      const res = await apiFetch(`/api/bots/${botId}/send-invite`, {
        method: 'POST',
        body: JSON.stringify({ phone: fullNumber, inviterName: userName }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus('sent');
        setPhone('');
        setTimeout(() => setStatus('idle'), 4000);
      } else {
        setErrMsg(data.friendly || data.error || 'Failed to send');
        if (data.action && data.actionUrl) setErrAction({ label: data.action, url: data.actionUrl });
        setStatus('error');
      }
    } catch {
      setErrMsg('Network error — try again');
      setStatus('error');
    }
  }

  const busy    = status === 'sending';
  const canSend = !!phone.trim() && !busy;

  return (
    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--card-border)' }}>
      <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '8px' }}>
        📲 Send invite to phone number
      </div>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <select
          value={dialCode}
          onChange={e => setDialCode(e.target.value)}
          style={{
            background: 'var(--bg-element)', border: '1px solid var(--card-border)',
            borderRadius: '6px', padding: '5px 6px', fontSize: '11px',
            color: 'var(--text-primary)', cursor: 'pointer', flexShrink: 0,
          }}
        >
          {DIAL_CODES.map(d => <option key={d.code} value={d.code}>{d.label}</option>)}
        </select>
        <input
          type="tel"
          placeholder="77 123 4567"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && canSend && handleSend()}
          style={{
            flex: 1, background: 'var(--bg-element)', border: '1px solid var(--card-border)',
            borderRadius: '6px', padding: '5px 10px', fontSize: '12px',
            color: 'var(--text-primary)', outline: 'none', minWidth: 0,
          }}
        />
        <button
          onClick={handleSend}
          disabled={!canSend}
          style={{
            background: status === 'sent' ? '#10b981' : '#5B4FE8', color: 'white', border: 'none',
            borderRadius: '6px', padding: '5px 12px', fontSize: '12px', fontWeight: '500',
            cursor: canSend ? 'pointer' : 'default', whiteSpace: 'nowrap',
            opacity: canSend ? 1 : 0.5, transition: 'background 0.2s',
          }}
        >
          {status === 'sending' ? '...' : status === 'sent' ? '✓ Sent!' : 'Send Invite'}
        </button>
      </div>
      {status === 'error' && (
        <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '5px' }}>
          ⚠ {errMsg}
          {errAction && (
            <> · <a href={errAction.url} target="_blank" rel="noreferrer" style={{ color: '#ef4444', textDecoration: 'underline' }}>{errAction.label} ↗</a></>
          )}
        </div>
      )}
      {status === 'sent' && (
        <div style={{ fontSize: '11px', color: '#10b981', marginTop: '5px' }}>Message sent via WhatsApp.</div>
      )}
    </div>
  );
}
